import { Validator } from 'vee-validate';
import moment from 'moment';
import store from '../store/store';

/**
 * Custom validation rule to test for at least one numeric character.
 */
const customValidatorRules = {
  dispatch_action: {
    validate() {
      return true;
    },
  },
  has_time_between: {
    validate: (value, args) => {
      // Only do validation for the 'high' amount
      let doesValidate = (args[0] === 'high'); // TODO: Use config value

      if (
        doesValidate &&
          document.getElementById(sessionStorage.getItem('found_date_low')) &&
          document.getElementById(sessionStorage.getItem('found_date_high'))
      ) {
        let dateLow = moment(document.getElementById(sessionStorage.getItem('found_date_low')).value, 'MM/DD/YYYY'); // TODO: Use proper date values
        const dateHigh = moment(document.getElementById(sessionStorage.getItem('found_date_high')).value, 'MM/DD/YYYY'); // TODO: Use proper date values
        const dateToday = moment();

        // Time lapse before today doesn't matter, so choose whichever is greater
        if (dateLow.isBefore(dateToday)) {
          dateLow = dateToday;
        }

        // Time lapse happens when there is more than 1 day between the two dates
        if (dateLow > dateToday && (dateHigh - dateLow) > 86400000) { // 1 day = 86400000 milliseconds
          window.console.warn('Warning: There is a time lapse between the high and low dates');

          return false;
        }
      }

      return true;
    },
  },
  has_compare_integers: {
    validate: (value, args) => {
      // Only do validation for the 'high' amount
      let doesValidate = (args[0] === 'high'); // TODO: Use config value

      if (
        doesValidate &&
          document.getElementById(sessionStorage.getItem('found_integer_low')) &&
          document.getElementById(sessionStorage.getItem('found_integer_high'))
      ) {
        const integerLow = parseInt(document.getElementById(sessionStorage.getItem('found_integer_low')).value, 10);
        const integerHigh = parseInt(document.getElementById(sessionStorage.getItem('found_integer_high')).value, 10);

        if (integerLow > integerHigh) {
          window.console.warn('Warning: The low integer is higher than the high integer.');

          return false;
        }
      }

      return true;
    },
  },
  /**
   * Custom validation rule similar to 'required' but is needed for its custom error message.
   * Field names should not be referenced directly since they are allowed to change, so we cannot
   * use Vee Validate's built-in field-level error messaging.
   */
  required_checkbox: {
    validate(value, invalidateFalse = true) {
      if (Array.isArray(value) && value.length === 0) { // Copied from Validator's `isEmptyArray`
        return false;
      }

      // incase a field considers `false` as an empty value like checkboxes.
      if (value === false && invalidateFalse) {
        return false;
      }

      if (value === undefined || value === null) {
        return false;
      }

      return true;
    },
  },
  /**
   * Custom validation rule to test for at least one uppercase alpha character.
   */
  has_alpha_uppercase: {
    validate(value) {
      return (!!value.match('[A-Z]'));
    },
  },
  isEqualTo: {
    validate: (userInput, args) => {
      if (!args) {
        return Promise.resolve({
          valid: false,
          message: 'isEqualTo rule has missing arguments',
        });
      }

      const valueToComapareTo = args[0];
      return new Promise(resolve => resolve({
        valid: valueToComapareTo === userInput,
      }));
    },
    getMessage: (field, params, data) => data.message,
  },
  /**
   * Custom validation rule to test for at least one lowercase alpha character.
   */
  has_alpha_lowercase: {
    validate(value) {
      return (!!value.match('[a-z]'));
    },
  },
  /**
   * Custom validation rule to test for at least one numeric character.
   */
  has_numeric: {
    validate(value) {
      return (!!value.match('[0-9]'));
    },
  },
  /**
   * Custom validation rule to test for at least one character in this list:
   *
   * !"#$%&'()*+,-./:;<=>?@[]^_`{|}~
   */
  has_special_char: {
    validate(value) {
      return (!!value.match('[!"#$%&\'()*+,-./:;<=>?@^_`{|}~\\[\\]]'));
    },
  },
  /**
   * Custom validation rule to test for at 3 of 4 password requirements.
   * 1 upper case characters (A through Z)
   * 1 lower case characters (a through z)
   * Base 10 digits (0 through 9)
   * Non-alphabetic characters (i.e., !,$,#,%)
   * (The minimum of 8 characters is a mandatory requirement and is applied in a separate rule)
   */
  has_met_password_standards: {
    validate(value) {
      let reg = /(?:\W)/gm;

      let capital = (value.match('[A-Z]')) ? 1 : 0;
      let lowercase = (value.match('[a-z]')) ? 1 : 0;
      let digit = (value.match('[0-9]')) ? 1 : 0;
      let special = (value.match(reg)) ? 1 : 0;

      let total = capital + lowercase + digit + special;
      return (total >= 3);
    },
  },
  /**
   * Custom validation rule to test whether two input fields
   * contain matching values, such as password confirmation.
   */
  confirmed_match: {
    validate: (value, [other]) => {
      // Using store since VeeValidate's `hasTarget` can't see other components.
      let otherValue = store.mutatable_data[other] || {};

      return value === otherValue.field_value;
    },
  },
  creditCardNumberField: {
    validate: () => true,
  },
  /**
   * Custom validation rule to test whether a given date is within
   * the time period between two given years from the present. This
   * requires moment.js .
   */
  within_year_range: {
    validate(value, args) {
      let min; // Min years ago, which is the maximum date
      let max; // Max years ago, which is the minimum date
      let format; // Built-in rule 'date_between' requires the date format
      let minDate;
      let maxDate;
      let dateValue;
      let isItBetween;
      /* eslint prefer-const: */
      [min, max, format] = args;
      dateValue = moment(value).format('MM/DD/YYYY');
      minDate = moment().subtract(min, 'years').format(format);
      maxDate = moment().subtract(max, 'years').format(format);

      isItBetween = Validator.rules.date_between(dateValue, [
        minDate,
        maxDate,
        format,
      ]);

      return isItBetween;
    },
  },
  /**
   * Custom validation rule to test whether a given date is within
   * the time period between two given years. This requires moment.js .
   */
  within_year_date_range: {
    validate(value, args) {
      let min; // Minimum date
      let max; // Maximum date, or if less than min year then # of years into the future
      let format; // Built-in rule 'date_between' requires the date format
      let minDate;
      let maxDate;
      let isItBetween;

      [min, max, format] = args;
      minDate = min; // Simply use the min value as the minimum year
      /**
         * In the case that the max year is less than the min year,
         * use the value of 'max' as the number of years from present
         * into the future.
         */
      if (max < min) {
        maxDate = moment().add(max, 'years').format(format);
      } else {
        maxDate = max; // Simply use the max value as the maximum year
      }
      isItBetween = Validator.rules.date_between(value, [
        minDate,
        maxDate,
        true,
        format,
      ]);

      return isItBetween;
    },
  },
  /**
   * Custom validation rule to test whether a given date is within
   * the time period between two given values and their units of time.
   * This requires moment.js .
   */
  within_custom_date_range: {
    validate(value, args) {
      let min; // Minimum magnitude (used in conjunction with time_unit_min)
      let max; // Maximum magnitude (used in conjunction with time_unit_max)
      let timeUnitMin; // Unit of time (days, weeks, months, years)
      let timeUnitMax; // Unit of time (days, weeks, months, years)
      let format; // Built-in rule 'date_between' requires the date format
      let minDate;
      let maxDate;
      let dateValue;
      let isItBetween;
      dateValue = moment(value).format('MM/DD/YYYY');
      [min, max, timeUnitMin, timeUnitMax, format] = args;
      minDate = moment().subtract(min, timeUnitMin).format(format);
      maxDate = moment().add(max, timeUnitMax).format(format);
      isItBetween = Validator.rules.date_between(dateValue, [
        minDate,
        maxDate,
        true,
        format,
      ]);
      return isItBetween;
    },
  },
  within_custom_date_range_reports: {
    validate(value, args) {
      let min; // Minimum magnitude (used in conjunction with time_unit_min)
      let max; // Maximum magnitude (used in conjunction with time_unit_max)
      let timeUnitMin; // Unit of time (days, weeks, months, years)
      let timeUnitMax; // Unit of time (days, weeks, months, years)
      let format; // Built-in rule 'date_between' requires the date format
      let minDate;
      let maxDate;
      let dateValue;
      let isItBetween;
      dateValue = moment(value).format('MM/DD/YYYY');
      [min, max, timeUnitMin, timeUnitMax, format] = args;
      minDate = moment().subtract(min, timeUnitMin).format(format);
      maxDate = moment().add(max, timeUnitMax).format(format);
      isItBetween = Validator.rules.date_between(dateValue, [
        minDate,
        maxDate,
        true,
        format,
      ]);
      return isItBetween;
    },
  },
  /**
   * Almost the same as `within_custom_date_range` but for MM/YYYY such as credit card exp. date.
   * TODO: Refactor into `within_custom_date_range`.
   */
  within_custom_date_range_exp: {
    validate(value, args) {
      let min; // Minimum magnitude (used in conjunction with time_unit_min)
      let max; // Maximum magnitude (used in conjunction with time_unit_max)
      let timeUnitMin; // Unit of time (days, weeks, months, years)
      let timeUnitMax; // Unit of time (days, weeks, months, years)
      let format; // Built-in rule 'date_between' requires the date format
      let minDate;
      let maxDate;
      let dateValue;
      let isItBetween;
      dateValue = moment(value, 'MM/YYYY').format('MM/YYYY');
      [min, max, timeUnitMin, timeUnitMax, format] = args;
      minDate = moment().subtract(min, timeUnitMin).format(format);
      maxDate = moment().add(max, timeUnitMax).format(format);
      isItBetween = Validator.rules.date_between(dateValue, [
        minDate,
        maxDate,
        true,
        format,
      ]);

      return isItBetween;
    },
  },
  required_privacy_checkbox: {
    validate(value, invalidateFalse = true) {
      if (Array.isArray(value) && value.length === 0) { // Copied from Validator's `isEmptyArray`
        return false;
      }

      // incase a field considers `false` as an empty value like checkboxes.
      if (value === false && invalidateFalse) {
        return false;
      }

      if (value === undefined || value === null) {
        return false;
      }

      return true;
    },
  },
};

export default customValidatorRules;



// WEBPACK FOOTER //
// ./src/services/validatorRules.js